import {isFeatureEnabled} from '@github-ui/feature-flags'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {useMatch, useParams} from '@github-ui/react-router'
import {ThreePanesLayout} from '@github-ui/three-panes-layout'
import {useSearchParams} from '@github-ui/use-navigate'
import {Button} from '@primer/react'
import {useCallback, useMemo} from 'react'

import {NewProjectForm} from '../components/projects/NewProjectForm'
import {
  DEFAULT_PROJECT_SORT,
  isProjectSortValue,
  type ProjectSortValue,
} from '../components/projects/project-sort-options'
import {ProjectList} from '../components/projects/ProjectList'
import {ProjectsActions} from '../components/projects/ProjectsActions'
import {ProjectSearchInput} from '../components/projects/ProjectSearchInput'
import {ProjectsTemplatesToggle} from '../components/projects/ProjectsTemplatesToggle'
import styles from '../components/projects/RepositoryProjects.module.css'
import {useCachedProjectsOwnerContext} from '../components/projects/use-cached-projects-owner-context'
import {useProjects} from '../components/projects/use-projects'
import {useShowTemplatesIndex} from '../components/projects/use-show-templates-index'
import {getRepoIssuesSidebarPanes} from '../components/sidebar/get-repo-issues-sidebar-panes'
import {AnalyticsWrapper} from './AnalyticsWrapper'

export function RepositoryProjectsPage() {
  const {owner = '', repo = ''} = useParams<{owner: string; repo: string}>()
  const [searchParams, setSearchParams] = useSearchParams()
  const state = searchParams.get('state') === 'closed' ? 'closed' : 'open'
  const query = searchParams.get('q') ?? ''
  const sortParam = searchParams.get('sort')
  const sort = isProjectSortValue(sortParam) ? sortParam : DEFAULT_PROJECT_SORT
  // URL is the single source of truth for which view we're on, mirroring how `sort`
  // and `q` work.
  const isTemplatesView = useMatch('/:owner/:repo/projects/templates') !== null
  const mode: 'projects' | 'templates' = isTemplatesView ? 'templates' : 'projects'
  const baseProjectsUrl = useMemo(() => `/${owner}/${repo}/projects`, [owner, repo])

  const {data, isPending, isError, isFetchingNextPage, isPlaceholderData, hasNextPage, fetchNextPage} = useProjects({
    owner,
    repo,
    state,
    query: query || undefined,
    sort,
    isTemplateView: isTemplatesView,
  })
  // Read the templates flag from any cached sibling response so the toggle stays
  // mounted across the remount when toggling Projects ↔ Templates.
  const cachedShowTemplatesIndex = useShowTemplatesIndex(owner, repo)
  // Same trick for the header CTA's owner-context fields: without this the New project
  // button would unmount during the in-flight pending state on the new route.
  const cachedOwnerContext = useCachedProjectsOwnerContext(owner, repo)

  const handleQueryChange = useCallback(
    (next: string) => {
      const trimmed = next.trim()
      const params = new URLSearchParams(searchParams)
      if (trimmed) {
        params.set('q', trimmed)
      } else {
        params.delete('q')
      }
      setSearchParams(params, {replace: true, preventAutofocus: true})
    },
    [searchParams, setSearchParams],
  )

  const handleSortChange = useCallback(
    (next: ProjectSortValue) => {
      const params = new URLSearchParams(searchParams)
      if (next === DEFAULT_PROJECT_SORT) {
        params.delete('sort')
      } else {
        params.set('sort', next)
      }
      setSearchParams(params, {replace: true, preventAutofocus: true})
    },
    [searchParams, setSearchParams],
  )

  if (!isFeatureEnabled('issue_pinned_views')) {
    return null
  }

  const firstPage = data?.pages[0]
  const projects = data?.pages.flatMap(page => page.projects) ?? []
  const sidebarPanes = getRepoIssuesSidebarPanes()
  const showTemplatesToggle = (firstPage?.showTemplatesIndex ?? cachedShowTemplatesIndex) === true
  const templatesToggle = showTemplatesToggle ? <ProjectsTemplatesToggle mode={mode} baseUrl={baseProjectsUrl} /> : null
  const viewerCanPush = firstPage?.viewerCanPush ?? cachedOwnerContext?.viewerCanPush ?? false
  const newProjectAction = firstPage?.newProjectAction ?? cachedOwnerContext?.newProjectAction ?? null
  const trailingHeaderContent = (
    <div className={styles.headerTrailing}>
      {templatesToggle}
      {newProjectAction && (
        <NewProjectForm owner={owner} repo={repo} action={newProjectAction}>
          <Button type="submit" variant="primary">
            New project
          </Button>
        </NewProjectForm>
      )}
    </div>
  )

  return (
    <AnalyticsWrapper category="Repository Projects Index">
      <ThreePanesLayout
        contentAs="div"
        resizeable={false}
        leftPaneWidth="medium"
        leftPane={sidebarPanes.leftPane}
        collapsibleLeftPane={sidebarPanes.collapsibleLeftPane}
        middlePane={
          <div className={styles.middlePaneWrapper}>
            <ErrorBoundary
              boundaryName="RepositoryProjectsPage"
              fallback={<p>Something went wrong loading projects.</p>}
            >
              <ProjectsActions heading={isTemplatesView ? 'Templates' : 'Projects'} trailing={trailingHeaderContent} />
              <ProjectSearchInput query={query} onQueryChange={handleQueryChange} />
              {isError ? (
                <p role="alert">Something went wrong loading projects. Please try again.</p>
              ) : (
                // Always render `ProjectList` so the toolbar stays mounted across the
                // cross-route remount when toggling Projects ↔ Templates.
                <ProjectList
                  projects={projects}
                  openCount={firstPage?.openCount ?? 0}
                  closedCount={firstPage?.closedCount ?? 0}
                  hasNextPage={hasNextPage}
                  loadingMore={isFetchingNextPage}
                  onLoadMore={() => {
                    void fetchNextPage()
                  }}
                  viewerCanPush={viewerCanPush}
                  owner={owner}
                  repo={repo}
                  newProjectAction={newProjectAction}
                  query={query}
                  state={state}
                  sort={sort}
                  onSortChange={handleSortChange}
                  isRefetching={isPlaceholderData}
                  isLoading={isPending}
                  isTemplateView={isTemplatesView}
                />
              )}
            </ErrorBoundary>
          </div>
        }
      />
    </AnalyticsWrapper>
  )
}
